import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { canAccess } from '@/lib/permissions';

export interface ProtectedRouteProps {
  children: ReactNode;
  /** If set, only roles in this list can access. */
  roles?: Array<'owner' | 'cashier'>;
}

export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { user, loading, configValid } = useAuth();

  if (!configValid) {
    return <Navigate to="/config-required" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-small text-ink-subtle">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

export function RoleGate({
  children,
  roles,
  fallback = null,
}: {
  children: ReactNode;
  roles: Array<'owner' | 'cashier'>;
  fallback?: ReactNode;
}) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <>{fallback}</>;
  return <>{children}</>;
}

export function useCanAccess() {
  const { claims } = useAuth();
  return (pathname: string) => canAccess(claims, pathname);
}
